import { fromBaseClone, fromBaseRecord, fromBaseUuid } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';
import type { FromBasePlayerResourceMetadata } from './from-base-player-resources';
import { createFromBaseNpcGrantContext } from './from-base-npc-stages';
import { createSkillRequirementChecker } from './skill-requirements';
import { compileCreatureMainWeaponDamageType } from './creature-main-weapon-damage-type';
import { readItemUuidSlot, readInventoryField } from './inventory-raw-readers';
import { FROM_BASE_STATIC_REGISTRIES } from './from-base-catalog-normalization';
import { appendCompiledLayerInPlace } from './status-row-construction';
import { constructNpcForcedDefensesFromBase } from './from-base-npc-forced-defenses';
import { constructInventoryReferenceShell } from './from-base-inventory-references';
import { constructActorAbilitiesFromBase } from './from-base-player-abilities';
import { phpJsonInt, phpJsonString, phpJsonTruthy } from './php-json-values';
import { constructFromBaseReachWatch } from './from-base-reach-watch';

export function npcMainWeaponDamageTypeFromRow(host: Row, readers: ReturnType<typeof createFromBaseNpcGrantContext>['readers']): string {
    return compileCreatureMainWeaponDamageType({
        mainHandWeaponCount: phpJsonInt(readers.layerValue(host, 'count_main_hand_weapon_equipped')),
        offHandWeaponCount: phpJsonInt(readers.layerValue(host, 'count_off_hand_weapon_equipped')),
        bothHandsWeaponCount: phpJsonInt(readers.layerValue(host, 'count_both_hands_weapon_equipped')),
        inventoryRows: (Array.isArray(host.inventory) ? host.inventory : []).filter(fromBaseRecord).map(row => ({
            equippedSlot: readItemUuidSlot(readInventoryField(row, 'equipped')),
            itemType: phpJsonString(readers.itemField(row, 'item_type')),
            damageType: readItemUuidSlot(readers.layerValue(row, 'damage_type')) || null,
        })) });
}

/** Template filtering and the shared live-actor charge/materialization tail. */
export function constructNpcTemplateAbilitiesFromBase(base: Row, metadata: FromBasePlayerResourceMetadata, input: Row): Row {
    const host = fromBaseClone(input), { deps, readers, definition, conditions } = createFromBaseNpcGrantContext(base, metadata, host, 'ability-spell-finalization');
    const ec = fromBaseRecord(host.encounter_creatures) ? host.encounter_creatures : null;
    const live = fromBaseUuid(readers.itemField(ec, 'encounter_creature_id'));
    const computed = (host.computed ??= {}) as Row, vitality = (computed.vitality ??= {}) as Row;
    const shellMetadata = { ...metadata.inventoryMetadata!, shellRegistries: { ...metadata.inventoryMetadata?.shellRegistries,
        vitality_status: FROM_BASE_STATIC_REGISTRIES.vitality_status!, damage_type: FROM_BASE_STATIC_REGISTRIES.damage_type! } };
    if (readers.itemField(vitality, 'v_status') === null) {
        const fresh = metadata.vitality.vitalityCatalog.find(row => row.variable_name === 'fresh');
        const id = readers.itemField(fresh ?? null, 'item_id');
        if (fromBaseUuid(id)) vitality.v_status = constructInventoryReferenceShell(id, 'vitality_status', shellMetadata, definition) ?? id;
    }
    const creature = (computed.creature ??= {}) as Row;
    if (!live && phpJsonTruthy(readers.layerValue(host, 'legendary_creature'))) appendCompiledLayerInPlace(host, 'ability', '8BA69C40-9191-8AF6-D521-5BF6A31F989E');
    const damage = npcMainWeaponDamageTypeFromRow(host, readers);
    creature.combat = { ...(fromBaseRecord(creature.combat) ? creature.combat : {}),
        main_weapon_damage_type: constructInventoryReferenceShell(damage, 'damage_type', shellMetadata, definition) ?? damage };
    if (live) {
        const result = constructActorAbilitiesFromBase({ entity_id: String(readers.itemField(base, 'entity_id')),
            ...(Array.isArray(base.charges) ? { charges: base.charges as Row[] } : {}) }, metadata, host,
            createFromBaseNpcGrantContext(base, metadata, host, 'ability-spell-finalization'));
        constructNpcForcedDefensesFromBase(base, metadata, result);
        constructFromBaseReachWatch(result, readers, definition);
        return result;
    }
    const skill = createSkillRequirementChecker(deps);
    for (const type of metadata.abilityTypes) {
        // Native filters only root buckets; authored/provider lists remain in the pristine store.
        const list = host[type]; if (list === null || typeof list !== 'object' || !Object.keys(list).length) continue;
        const filtered: unknown[] = [];
        for (const entry of Object.values(list)) {
            const ref = fromBaseRecord(entry) ? entry.item_arr ?? entry.item_id ?? entry : entry;
            const row = fromBaseRecord(ref) ? ref : fromBaseUuid(ref) ? definition(ref) : null;
            if (row && skill(row, host)) filtered.push(entry);
            else if (type === 'passive_ability' && fromBaseRecord(entry)) filtered.push({ ...entry, passive_conditions_active: false });
        }
        if (filtered.length) host[type] = filtered; else delete host[type];
    }
    if (host.passive_ability !== null && typeof host.passive_ability === 'object') for (const entry of Object.values(host.passive_ability)) {
        if (!fromBaseRecord(entry)) continue;
        const ref = readers.itemField(entry, 'item_arr') ?? readers.itemField(entry, 'item_id');
        const row = fromBaseRecord(ref) ? ref : fromBaseUuid(readItemUuidSlot(ref)) ? definition(readItemUuidSlot(ref)) : null;
        if (row && Object.keys(row).length) entry.passive_conditions_active = conditions.check(row, host);
    }
    constructNpcForcedDefensesFromBase(base, metadata, host);
    return host;
}
